document.addEventListener('DOMContentLoaded', () => {
  const rotateHandle = document.getElementById('rotate-handle');
  const rotationSlider = document.getElementById('rotation-slider');
  const canvas = document.getElementById('result-canvas');
  const messageArea = document.getElementById('message-area');
  const rugSizeNote = document.getElementById('rug-size-note');
  const rugWidthFeet = document.getElementById('rug-width-feet');
  const rugWidthInches = document.getElementById('rug-width-inches');
  const rugHeightFeet = document.getElementById('rug-height-feet');
  const rugHeightInches = document.getElementById('rug-height-inches');

  if (!rotateHandle || !rotationSlider || !canvas) return;

  rotateHandle.classList.remove('visible');

  const container = rotateHandle.parentElement;
  let lastCorners = null;
  let rugCenterClient = { x: null, y: null };

  // Cache handle half-size so drag frames avoid repeated layout reads.
  let handleHalfW = 22;
  let handleHalfH = 22;

  function updateHandleMetrics() {
    const hr = rotateHandle.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(rotateHandle);
    const fallbackW = parseFloat(computedStyle.width) || 44;
    const fallbackH = parseFloat(computedStyle.height) || 44;
    handleHalfW = (hr.width > 0 ? hr.width : fallbackW) / 2;
    handleHalfH = (hr.height > 0 ? hr.height : fallbackH) / 2;
  }

  // ─── Cached rect ─────────────────────────────────────────────────────────────
  // Reading getBoundingClientRect() forces a layout reflow. On low-end mobile
  // this is the single biggest frame-time killer. Cache it and only invalidate
  // on resize / scroll (both are rare events relative to pointermove).
  let _cachedCRect = null;
  let _cachedCanvRect = null;
  let _rectDirty = true;

  function invalidateRects() {
    _rectDirty = true;
    updateHandleMetrics();
  }

  function getRects() {
    if (_rectDirty || !_cachedCRect) {
      _cachedCRect   = container.getBoundingClientRect();
      _cachedCanvRect = canvas.getBoundingClientRect();
      _rectDirty = false;
    }
    return { cRect: _cachedCRect, canvRect: _cachedCanvRect };
  }

  // Always invalidate on resize and scroll so rects stay accurate.
  window.addEventListener('resize', () => {
    invalidateRects();
    handleAutoRotate();
  }, { passive: true });
  window.addEventListener('scroll', invalidateRects, { passive: true });

  // ─── Auto-rotate on Mobile ───────────────────────────────────────────────────
  window.isCanvasAutoRotated = function() {
    return window.innerWidth <= 768 && window.innerHeight > window.innerWidth && canvas && canvas.width > canvas.height;
  };

  function handleAutoRotate() {
    if (!canvas) return;

    if (window.isCanvasAutoRotated()) {
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      
      canvas.style.transformOrigin = 'center center';
      canvas.style.transform = `rotate(90deg)`;
      canvas.style.width = `${screenH}px`;
      canvas.style.height = `${screenW}px`;
      canvas.style.maxWidth = `${screenH}px`;
      canvas.style.maxHeight = `${screenW}px`;
      canvas.style.position = 'absolute';
    } else {
      canvas.style.transform = '';
      canvas.style.width = '';
      canvas.style.height = '';
      canvas.style.maxWidth = '';
      canvas.style.maxHeight = '';
      canvas.style.position = '';
    }
  }

  // Observe canvas for width/height changes (when photo loads)
  const canvasObserver = new MutationObserver(() => {
    handleAutoRotate();
    invalidateRects();
  });
  canvasObserver.observe(canvas, { attributes: true, attributeFilter: ['width', 'height'] });

  const demoWrap = document.querySelector('.visualizer-demo-wrap');
  const visualizerModalContent = document.getElementById('see-in-room-modal');
  if (demoWrap && visualizerModalContent) {
    const hideDemoOnTap = () => {
      demoWrap.classList.add('hidden-by-tap');
      visualizerModalContent.removeEventListener('pointerdown', hideDemoOnTap);
    };
    visualizerModalContent.addEventListener('pointerdown', hideDemoOnTap, { passive: true });
  }

  updateHandleMetrics();

  // ─── Coarse-pointer detection (cached – never changes at runtime) ─────────────
  const isCoarsePointer = !!(
    window.matchMedia &&
    window.matchMedia('(hover: none) and (pointer: coarse)').matches
  );

  // ─── updateHandlePosition ────────────────────────────────────────────────────
  // Called inside a RAF so it must be as cheap as possible.
  // Key change: we read canvas.width / canvas.height ONCE, build all derived
  // values once, and avoid any further DOM reads inside branches.
  function updateHandlePosition() {
    const { cRect, canvRect } = getRects();
    if (!canvRect.width || !canvRect.height) return;

    const halfW = handleHalfW;
    const halfH = handleHalfH;

    // Image dimensions from canvas element properties (no reflow).
    const imgW = canvas.width  || canvRect.width;
    const imgH = canvas.height || canvRect.height;

    const scale   = Math.min(canvRect.width / imgW, canvRect.height / imgH);
    const drawW   = imgW * scale;
    const drawH   = imgH * scale;
    const offsetX = (canvRect.width  - drawW) / 2;
    const offsetY = (canvRect.height - drawH) / 2;

    const imageLeft   = canvRect.left + offsetX;
    const imageTop    = canvRect.top  + offsetY;
    const imageRight  = imageLeft + drawW;
    const imageBottom = imageTop  + drawH;

    const pad      = isCoarsePointer ? 4 : 8;
    let minCenterX = imageLeft   + halfW + pad;
    let maxCenterX = imageRight  - halfW - pad;
    let minCenterY = imageTop    + halfH + pad;
    let maxCenterY = imageBottom - halfH - pad;

    if (minCenterX > maxCenterX) {
      const cx = (canvRect.left + canvRect.right) / 2;
      minCenterX = maxCenterX = cx;
    }
    if (minCenterY > maxCenterY) {
      const cy = (canvRect.top + canvRect.bottom) / 2;
      minCenterY = maxCenterY = cy;
    }

    // ── During drag: follow pointer, clamp to canvas bounds ────────────────
    if (isDragging && dragClient.x !== null && dragClient.y !== null) {
      const dragMinX = canvRect.left  + halfW;
      const dragMaxX = canvRect.right - halfW;
      const dragMinY = canvRect.top   + halfH;
      const dragMaxY = canvRect.bottom - halfH;

      const clampedX = Math.min(Math.max(dragClient.x, dragMinX), dragMaxX);
      const clampedY = Math.min(Math.max(dragClient.y, dragMinY), dragMaxY);

      // Use transform instead of left/top to avoid triggering layout.
      // Translate the element to the correct canvas-relative position.
      const relX = clampedX - cRect.left;
      const relY = clampedY - cRect.top;
      rotateHandle.style.left = '0px';
      rotateHandle.style.top  = '0px';
      rotateHandle.style.transform = `translate(${relX - halfW}px, ${relY - halfH}px)`;
      return;
    }

    // ── Helper: image-space → client coords ────────────────────────────────
    function imgToClient(px, py) {
      if (window.isCanvasAutoRotated && window.isCanvasAutoRotated()) {
        const centerClientX = canvRect.left + canvRect.width / 2;
        const centerClientY = canvRect.top + canvRect.height / 2;
        const relPx = (px - imgW / 2) * scale;
        const relPy = (py - imgH / 2) * scale;
        return {
          clientX: centerClientX - relPy,
          clientY: centerClientY + relPx
        };
      }
      return {
        clientX: canvRect.left + offsetX + px * scale,
        clientY: canvRect.top  + offsetY + py * scale,
      };
    }

    // ── Projected corners available ─────────────────────────────────────────
    if (lastCorners && lastCorners.length === 4) {
      const [c1, c2, c3, c4] = lastCorners;

      const mids = [
        [(c1[0] + c2[0]) / 2, (c1[1] + c2[1]) / 2],
        [(c2[0] + c3[0]) / 2, (c2[1] + c3[1]) / 2],
        [(c3[0] + c4[0]) / 2, (c3[1] + c4[1]) / 2],
        [(c4[0] + c1[0]) / 2, (c4[1] + c1[1]) / 2],
      ];

      const margin = halfH * 2;
      let bestInside = null;

      for (const m of mids) {
        if (
          m[0] >= -margin && m[0] <= imgW + margin &&
          m[1] >= -margin && m[1] <= imgH + margin
        ) {
          if (!bestInside || m[1] > bestInside.m[1]) bestInside = { m, client: imgToClient(m[0], m[1]) };
        }
      }

      const centerImg = [
        (c1[0] + c2[0] + c3[0] + c4[0]) / 4,
        (c1[1] + c2[1] + c3[1] + c4[1]) / 4,
      ];
      const centerClient = imgToClient(centerImg[0], centerImg[1]);
      rugCenterClient.x = centerClient.clientX;
      rugCenterClient.y = centerClient.clientY;

      let sourceClient;
      if (bestInside) {
        sourceClient = bestInside.client;
      } else {
        // None visible — pick midpoint closest to canvas center.
        const ccx = canvRect.left + canvRect.width  / 2;
        const ccy = canvRect.top  + canvRect.height / 2;
        let bestDist = Infinity;
        for (const m of mids) {
          const c = imgToClient(m[0], m[1]);
          const d = (c.clientX - ccx) ** 2 + (c.clientY - ccy) ** 2;
          if (d < bestDist) { bestDist = d; sourceClient = c; }
        }
      }

      const centerX = Math.min(Math.max(sourceClient.clientX, minCenterX), maxCenterX);
      const centerY = Math.min(Math.max(sourceClient.clientY, minCenterY), maxCenterY);

      applyHandlePosition(centerX - cRect.left, centerY - cRect.top);
      return;
    }

    // ── Fallback: circular orbit around canvas centre ───────────────────────
    const cx     = canvRect.left + canvRect.width  / 2;
    const cy     = canvRect.top  + canvRect.height / 2;
    const angleDeg = parseFloat(rotationSlider.value) || 0;
    const rad    = angleDeg * Math.PI / 180;
    const radius = Math.min(canvRect.width, canvRect.height) * 0.35;

    const rawX = cx + radius * Math.cos(rad);
    const rawY = cy + radius * Math.sin(rad);

    const clampedX = Math.min(Math.max(rawX, minCenterX), maxCenterX);
    const clampedY = Math.min(Math.max(rawY, minCenterY), maxCenterY);

    rugCenterClient.x = cx;
    rugCenterClient.y = cy;

    applyHandlePosition(clampedX - cRect.left, clampedY - cRect.top);
  }

  // Write position using translate to avoid layout (composited layer move).
  function applyHandlePosition(relX, relY) {
    rotateHandle.style.left = '0px';
    rotateHandle.style.top  = '0px';
    rotateHandle.style.transform = `translate(${relX}px, ${relY}px) translate(-50%, -50%)`;
  }

  rotationSlider.addEventListener('input', () => {
    // Only reposition when NOT dragging (drag loop manages its own RAF).
    if (!isDragging) scheduleHandleUpdate();
  });

  window.addEventListener('resize', () => {
    invalidateRects();
    scheduleHandleUpdate();
  }, { passive: true });

  // ─── Batched handle-position scheduling ──────────────────────────────────────
  // Multiple callers (slider input, resize, corners event) may all fire in the
  // same frame. Guard with a flag so we only run one RAF per frame.
  let _positionRaf = null;
  function scheduleHandleUpdate() {
    if (_positionRaf) return;
    _positionRaf = requestAnimationFrame(() => {
      _positionRaf = null;
      updateHandlePosition();
    });
  }

  // ─── Show handle ──────────────────────────────────────────────────────────────
  function showRotateHandle() {
    if (!rotateHandle.classList.contains('visible')) {
      rotateHandle.classList.add('visible');
      rotateHandle.style.display = '';
      scheduleHandleUpdate();
    }
  }

  // ─── Drag state ───────────────────────────────────────────────────────────────
  let isDragging       = false;
  let startAngle       = 0;
  let startRot         = 0;
  let currentPointerId = null;
  let dragClient       = { x: null, y: null };
  let moveRaf          = null;
  let lastDispatchedRot = null; // avoid redundant slider dispatches

  function clientAngleToCenter(clientX, clientY) {
    const cx = (rugCenterClient.x !== null) ? rugCenterClient.x : (() => {
      const { canvRect } = getRects();
      return canvRect.left + canvRect.width / 2;
    })();
    const cy = (rugCenterClient.y !== null) ? rugCenterClient.y : (() => {
      const { canvRect } = getRects();
      return canvRect.top + canvRect.height / 2;
    })();
    let dx = clientX - cx;
    let dy = clientY - cy;
    if (window.isCanvasAutoRotated && window.isCanvasAutoRotated()) {
      const tmp = dx;
      dx = dy;
      dy = -tmp;
    }
    return Math.atan2(dy, dx);
  }

  rotateHandle.addEventListener('pointerdown', (ev) => {
    ev.preventDefault();
    if (typeof closeSidebar === 'function') closeSidebar();
    try { window.dispatchEvent(new Event('visualizerInteractionStarted')); } catch (_) {}
    invalidateRects();
    rotateHandle.setPointerCapture(ev.pointerId);
    currentPointerId = ev.pointerId;
    isDragging       = true;
    lastDispatchedRot = null;
    rotateHandle.classList.add('dragging');
    startAngle   = clientAngleToCenter(ev.clientX, ev.clientY);
    startRot     = parseFloat(rotationSlider.value) || 0;
    dragClient.x = ev.clientX;
    dragClient.y = ev.clientY;
  });

  // Use { passive: true } on pointermove — we never call preventDefault here,
  // and passive listeners are dispatched off the main thread on mobile.
  document.addEventListener('pointermove', (ev) => {
    if (!isDragging) return;
    if (currentPointerId !== null && ev.pointerId !== currentPointerId) return;

    dragClient.x = ev.clientX;
    dragClient.y = ev.clientY;

    if (moveRaf) return; // already have a frame queued
    moveRaf = requestAnimationFrame(() => {
      moveRaf = null;

      const angle    = clientAngleToCenter(dragClient.x, dragClient.y);
      let deltaDeg   = (angle - startAngle) * 180 / Math.PI;
      let newRot     = ((startRot - deltaDeg + 180) % 360) - 180;
      const rounded  = Math.round(newRot);

      if (rounded !== lastDispatchedRot) {
        lastDispatchedRot    = rounded;
        rotationSlider.value = rounded;
        // Dispatch a lightweight 'input' event — avoid bubbling overhead.
        rotationSlider.dispatchEvent(new Event('input'));
      }

      // Visual update happens here (not via the input listener during drag).
      updateHandlePosition();
    });
  }, { passive: true });

  document.addEventListener('pointerup', (ev) => {
    if (!isDragging) return;
    if (currentPointerId !== null && ev.pointerId !== currentPointerId) return;
    isDragging = false;
    currentPointerId = null;

    if (moveRaf) {
      cancelAnimationFrame(moveRaf);
      moveRaf = null;
    }

    rotateHandle.classList.remove('dragging');
    try { rotateHandle.releasePointerCapture(currentPointerId); } catch (_) {}
    rotationSlider.dispatchEvent(new Event('change', { bubbles: true }));

    rotateHandle.style.pointerEvents = 'none';
    let cleanedUp = false;

    function finish() {
      if (cleanedUp) return;
      cleanedUp = true;
      rotateHandle.removeEventListener('transitionend', onFadeOutEnd);
      rotateHandle.removeEventListener('transitionend', onFadeInEnd);
      dragClient.x = dragClient.y = null;
      invalidateRects();          // rects may have shifted after interaction
      scheduleHandleUpdate();     // reposition in next frame, not synchronously
      rotateHandle.style.opacity      = '';
      rotateHandle.style.pointerEvents = '';
    }

    function onFadeInEnd(e) {
      if (e.propertyName !== 'opacity') return;
      finish();
    }

    function onFadeOutEnd(e) {
      if (e.propertyName !== 'opacity') return;
      rotateHandle.removeEventListener('transitionend', onFadeOutEnd);
      dragClient.x = dragClient.y = null;
      scheduleHandleUpdate();
      requestAnimationFrame(() => {
        rotateHandle.addEventListener('transitionend', onFadeInEnd);
        rotateHandle.style.opacity = '';
      });
    }

    rotateHandle.addEventListener('transitionend', onFadeOutEnd);
    requestAnimationFrame(() => { rotateHandle.style.opacity = '0'; });

    // Safety fallback — if CSS transitions are disabled or very short.
    setTimeout(finish, 500);
  }, { passive: true });

  document.addEventListener('pointercancel', (ev) => {
    if (!isDragging) return;
    if (currentPointerId !== null && ev.pointerId !== currentPointerId) return;
    isDragging = false;
    currentPointerId = null;
    if (moveRaf) {
      cancelAnimationFrame(moveRaf);
      moveRaf = null;
    }
    dragClient.x = dragClient.y = null;
    rotateHandle.classList.remove('dragging');
    scheduleHandleUpdate();
  }, { passive: true });

  // ─── Keyboard accessibility ───────────────────────────────────────────────────
  rotateHandle.addEventListener('keydown', (ev) => {
    let delta = 0;
    if (ev.key === 'ArrowLeft')  delta = +5;
    else if (ev.key === 'ArrowRight') delta = -5;
    else return;

    ev.preventDefault();
    let v = ((parseInt(rotationSlider.value || '0', 10) + delta + 180) % 360) - 180;
    rotationSlider.value = v;
    rotationSlider.dispatchEvent(new Event('input',  { bubbles: true }));
    rotationSlider.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ─── Rug size note ────────────────────────────────────────────────────────────
  function formatSize() {
    const wf = (typeof window.getRugWidthFeet === 'function') ? String(Math.floor(window.getRugWidthFeet())) : (rugWidthFeet ? rugWidthFeet.value : '');
    const wi = (typeof window.getRugWidthFeet === 'function') ? String(Math.round(((window.getRugWidthFeet() || 0) % 1) * 12)) : (rugWidthInches ? rugWidthInches.value : '');
    const hf = (typeof window.getRugHeightFeet === 'function') ? String(Math.floor(window.getRugHeightFeet())) : (rugHeightFeet ? rugHeightFeet.value : '');
    const hi = (typeof window.getRugHeightFeet === 'function') ? String(Math.round(((window.getRugHeightFeet() || 0) % 1) * 12)) : (rugHeightInches ? rugHeightInches.value : '');
    const hasBaseSize = !!(wf || wi || hf || hi);
    const baseSize = hasBaseSize ? `Rug: ${wf || '?'}ft ${wi || '0'}in × ${hf || '?'}ft ${hi || '0'}in` : '';
    const recommendedSize = String(window.currentRugRecommendedSizeLabel || '').trim();
    if (window.currentRugRecommended === true || String(window.currentRugRecommended).toLowerCase() === 'true') {
      const label = recommendedSize ? `Recommended size: ${recommendedSize}` : 'Recommended size';
      return baseSize ? `${baseSize} <span class="rug-size-note__badge">(Recommended)</span>` : `${label} <span class="rug-size-note__badge">(Recommended)</span>`;
    }
    return baseSize || recommendedSize;
  }

  function updateRugSizeNote() {
    if (!rugSizeNote) return;
    rugSizeNote.innerHTML = formatSize();
  }

  [rugWidthFeet, rugWidthInches, rugHeightFeet, rugHeightInches].forEach(el => {
    if (!el) return;
    el.addEventListener('input',  updateRugSizeNote, { passive: true });
    el.addEventListener('change', updateRugSizeNote, { passive: true });
  });

  updateRugSizeNote();

  const sizeEvents = ['startAutomaticVisualization', 'visualizerClosed', 'rugSizeInputsUpdated'];
  sizeEvents.forEach(name => {
    document.addEventListener(name, updateRugSizeNote, { passive: true });
    window.addEventListener(name,   updateRugSizeNote, { passive: true });
  });

  const visualizerModal = document.getElementById('see-in-room-modal');
  if (visualizerModal && typeof MutationObserver !== 'undefined') {
    new MutationObserver(() => {
      if (visualizerModal.style.display !== 'none') updateRugSizeNote();
    }).observe(visualizerModal, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  // ─── Projected corners event ──────────────────────────────────────────────────
  document.addEventListener('rugProjectedCorners', (ev) => {
    try {
      const corners = ev && ev.detail && ev.detail.corners;
      if (!Array.isArray(corners) || corners.length !== 4) { lastCorners = null; return; }
      lastCorners = corners;
      invalidateRects();      // canvas may have resized when rug was rendered
      scheduleHandleUpdate();
      showRotateHandle();
    } catch (e) {
      console.warn('rugProjectedCorners handler error', e);
    }
  }, { passive: true });

  // ─── Visualizer Side Panel Logic ──────────────────────────────────────────────
  const vizSidePanel = document.getElementById('visualizer-side-panel');
  const vizSidePanelToggle = document.getElementById('visualizer-side-panel-toggle');
  const vizSidePanelClose = document.querySelector('.viz-side-panel__close');
  const vizSidePanelBackdrop = document.getElementById('visualizer-side-panel-backdrop');
  const vizSizeOptions = document.getElementById('vizSizeOptions');
  const vizAddToCartBtn = document.getElementById('viz-add-to-cart-btn');
  
  if (vizSidePanel && vizSizeOptions && visualizerModal && typeof MutationObserver !== 'undefined') {
    let sidePanelInitialized = false;
    let initialVariantId = null;
    let isFirstLoadOfSession = true;
    let pdpOriginalState = null;
    let currentSidePanelSelectedVariantId = null;

    function capturePdpOriginalState() {
      if (pdpOriginalState) return;
      const variantInput = document.querySelector('input[name="id"], select[name="id"], .variant-selector');
      const btn = document.getElementById('pdp-see-in-room');
      pdpOriginalState = {
        variantId: variantInput ? variantInput.value : null,
        rugVariantData: window.RugVariantData ? JSON.parse(JSON.stringify(window.RugVariantData)) : null,
        btnAttrs: btn ? {
          productId: btn.getAttribute('data-product-id'),
          productHandle: btn.getAttribute('data-product-handle'),
          rug: btn.getAttribute('data-rug'),
          size: btn.getAttribute('data-size'),
          shape: btn.getAttribute('data-shape'),
        } : null
      };
    }
    window.visualizerCapturePdpOriginalState = capturePdpOriginalState;

    function restorePdpOriginalState() {
      if (!pdpOriginalState) return;
      try {
        const variantInput = document.querySelector('input[name="id"], select[name="id"], .variant-selector');
        if (variantInput && pdpOriginalState.variantId) {
          if (variantInput.value !== pdpOriginalState.variantId) {
            variantInput.value = pdpOriginalState.variantId;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        if (pdpOriginalState.rugVariantData) {
          window.RugVariantData = pdpOriginalState.rugVariantData;
        }
        const btn = document.getElementById('pdp-see-in-room');
        if (btn && pdpOriginalState.btnAttrs) {
          const attrs = pdpOriginalState.btnAttrs;
          if (attrs.productId) btn.setAttribute('data-product-id', attrs.productId);
          if (attrs.productHandle) btn.setAttribute('data-product-handle', attrs.productHandle);
          if (attrs.rug) btn.setAttribute('data-rug', attrs.rug);
          if (attrs.size) btn.setAttribute('data-size', attrs.size);
          if (attrs.shape) btn.setAttribute('data-shape', attrs.shape);
        }
        console.log('✅ Restored PDP Original State upon closing visualizer');
      } catch (e) {
        console.warn('Failed to restore PDP original state', e);
      }
      pdpOriginalState = null;
      currentSidePanelSelectedVariantId = null;
      window.activeVisualizerButton = null;
      window.activeVisualizerSource = null;
      window.activeVisualizerProductHandle = null;
      window.activeVisualizerProductId = null;
      window.activeVisualizerRugUrl = null;
      window.activeVisualizerVariantData = null;
      window.activeVisualizerSize = null;
      window.activeVisualizerShape = null;
      window.activeVisualizerVariantId = null;
    }
    window.visualizerRestorePdpOriginalState = restorePdpOriginalState;

    let sidePanelAutoCloseTimer = null;

    function clearSidePanelAutoClose() {
      if (sidePanelAutoCloseTimer) {
        clearTimeout(sidePanelAutoCloseTimer);
        sidePanelAutoCloseTimer = null;
      }
    }

    function scheduleSidePanelAutoClose(delayMs = 5000) {
      clearSidePanelAutoClose();
      if (!vizSidePanel || vizSidePanel.getAttribute('aria-hidden') === 'true') return;
      sidePanelAutoCloseTimer = setTimeout(() => {
        sidePanelAutoCloseTimer = null;
        closeSidebar();
      }, delayMs);
    }

    function closeSidebar() {
      clearSidePanelAutoClose();

      if (!vizSidePanel) return;

      vizSidePanel.setAttribute('aria-hidden', 'true');

      if (vizSidePanelToggle) {
        vizSidePanelToggle.style.display = 'flex';
        vizSidePanelToggle.setAttribute('aria-expanded', 'false');
      }

      if (vizSidePanelBackdrop) {
        vizSidePanelBackdrop.setAttribute('aria-hidden', 'true');
      }
    }

    function ensureDesktopRecommendationNav(recsList) {
      if (!recsList || recsList.dataset.desktopNavBound === 'true') return;

      const container = recsList.parentElement;
      if (!container) return;

      container.style.position = 'relative';

      const makeBtn = (direction) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `viz-rec-scroll-btn viz-rec-scroll-btn--${direction}`;
        btn.setAttribute('aria-label', direction === 'left' ? 'Scroll recommendations left' : 'Scroll recommendations right');
        btn.innerHTML = direction === 'left' ? '&#8249;' : '&#8250;';

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const amount = Math.max(220, recsList.clientWidth * 0.8);

          recsList.scrollBy({
            left: direction === 'left' ? -amount : amount,
            behavior: 'smooth'
          });
        });

        return btn;
      };

      const leftBtn = makeBtn('left');
      const rightBtn = makeBtn('right');

      container.appendChild(leftBtn);
      container.appendChild(rightBtn);

      const updateButtons = () => {
        const maxScroll = recsList.scrollWidth - recsList.clientWidth;

        leftBtn.style.display =
          recsList.scrollLeft > 4 ? 'flex' : 'none';

        rightBtn.style.display =
          recsList.scrollLeft < maxScroll - 4 ? 'flex' : 'none';
      };

      recsList.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons, { passive: true });

      requestAnimationFrame(updateButtons);

      recsList.dataset.desktopNavBound = 'true';
    }

    function enableDesktopRecommendationScrolling(recsList) {
      if (
        !recsList ||
        recsList.dataset.desktopScrollBound === 'true'
      ) {
        return;
      }

      const isDesktopPointer = () =>
        window.matchMedia &&
        window.matchMedia(
          '(hover: hover) and (pointer: fine)'
        ).matches;

      // Mouse wheel / trackpad -> horizontal scroll
      recsList.addEventListener(
        'wheel',
        (event) => {
          if (!isDesktopPointer()) return;

          if (
            recsList.scrollWidth <=
            recsList.clientWidth
          ) {
            return;
          }

          const delta =
            Math.abs(event.deltaX) >
            Math.abs(event.deltaY)
              ? event.deltaX
              : event.deltaY;

          if (!delta) return;

          event.preventDefault();
          recsList.scrollLeft += delta;
        },
        { passive: false }
      );

      // Click-and-drag horizontal scrolling
      let dragging = false;
      let moved = false;
      let startX = 0;
      let startScrollLeft = 0;

      recsList.addEventListener('mousedown', (event) => {
        if (!isDesktopPointer() || event.button !== 0) return;
        if (recsList.scrollWidth <= recsList.clientWidth) return;

        dragging = true;
        moved = false;
        startX = event.clientX;
        startScrollLeft = recsList.scrollLeft;

        recsList.classList.add('is-desktop-dragging');
      });

      window.addEventListener('mousemove', (event) => {
        if (!dragging) return;

        const distance = event.clientX - startX;

        if (Math.abs(distance) > 4) {
          moved = true;
        }

        recsList.scrollLeft =
          startScrollLeft - distance;
      });

      window.addEventListener('mouseup', () => {
        if (!dragging) return;

        dragging = false;
        recsList.classList.remove('is-desktop-dragging');

        setTimeout(() => {
          moved = false;
        }, 0);
      });

      // Dragging must not accidentally select a rug.
      recsList.addEventListener(
        'click',
        (event) => {
          if (!moved) return;

          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      recsList.dataset.desktopScrollBound = 'true';
    }

    function openSidebar() {
      if (!vizSidePanel) return;

      vizSidePanel.setAttribute('aria-hidden', 'false');

      if (vizSidePanelToggle) {
        // Panel is already open, so Try More must not cover its contents.
        vizSidePanelToggle.style.display = 'none';
        vizSidePanelToggle.setAttribute('aria-expanded', 'true');
      }

      if (vizSidePanelBackdrop) {
        vizSidePanelBackdrop.setAttribute('aria-hidden', 'false');
      }
    }

    function toggleSidebar() {
      if (vizSidePanel && vizSidePanel.getAttribute('aria-hidden') === 'false') {
        closeSidebar();
      } else {
        openSidebar();
        clearSidePanelAutoClose();
      }
    }

    window.openVisualizerSidePanel = openSidebar;
    window.closeVisualizerSidePanel = closeSidebar;
    window.scheduleSidePanelAutoClose = scheduleSidePanelAutoClose;
    window.clearSidePanelAutoClose = clearSidePanelAutoClose;

    // c. Upon full rug warp finish, close it in 5 seconds
    window.addEventListener('visualizerRugWarpComplete', () => {
      scheduleSidePanelAutoClose(5000);
    });

    window.addEventListener('visualizerFirstRenderDone', () => {
      scheduleSidePanelAutoClose(5000);
    });

    // Before rug warp finishes / when a new warp starts: keep/make open
    window.addEventListener('startAutomaticVisualization', () => {
      clearSidePanelAutoClose();
      openSidebar();
    });

    window.addEventListener('visualizerOpened', () => {
      clearSidePanelAutoClose();
      openSidebar();
    });

    // b. Close side panel if visualizer interaction occurs (drag or rotate)
    window.addEventListener('visualizerInteractionStarted', () => {
      closeSidebar();
    });

    // Close side panel when touching/clicking outside the side panel in the visualizer
    document.addEventListener('pointerdown', (e) => {
      if (!vizSidePanel || vizSidePanel.getAttribute('aria-hidden') === 'true') return;
      if (vizSidePanel.contains(e.target) || (vizSidePanelToggle && vizSidePanelToggle.contains(e.target))) {
        return;
      }
      closeSidebar();
    }, { passive: true });

    window.addEventListener('visualizerVariantDataReady', () => {
      const data = window.activeVisualizerVariantData;
      if (data && data.sizes && Object.keys(data.sizes).length > 0) {
        data.isRecommendation = true;
        initializeSidePanel(data);
        updateActiveSidePanelButton();
        openSidebar();
      }
    });

    window.addEventListener('visualizerClosed', () => {
      clearSidePanelAutoClose();
      restorePdpOriginalState();
      sidePanelInitialized = false;
      initialVariantId = null;
      closeSidebar();
      window.activeVisualizerButton = null;
    });

    if (vizSidePanelToggle) {
      vizSidePanelToggle.addEventListener('click', toggleSidebar);
    }

    if (vizSidePanelClose) {
      vizSidePanelClose.addEventListener('click', closeSidebar);
    }

    if (vizSidePanelBackdrop) {
      vizSidePanelBackdrop.addEventListener('click', closeSidebar);
    }

    if (vizAddToCartBtn) {
      vizAddToCartBtn.addEventListener('click', () => {
        const variantInput = document.querySelector('input[name="id"], select[name="id"], .variant-selector');
        const currentVid = currentSidePanelSelectedVariantId || (variantInput ? variantInput.value : null);
        if (!currentVid) return;

        const siteAddToCart = document.querySelector('button[name="add"], .add-to-cart, #AddToCart');
        if (siteAddToCart && !currentSidePanelSelectedVariantId) {
          siteAddToCart.click();
        } else {
          fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: currentVid, quantity: 1 }] })
          })
          .then(res => res.json())
          .then(() => { window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart'; })
          .catch(err => console.error('Add to cart failed', err));
        }
      });
    }

    async function loadRecommendedProductVariants(activeBtn, recHandle) {
      try {
        const cleanHandle = String(recHandle || '').replace(/^owfam-/, '').replace(/^\/products\//, '').split('?')[0].trim();
        if (!cleanHandle) throw new Error('No handle provided');

        const handleCandidates = [
          cleanHandle,
          cleanHandle.replace(/^owfam-/, ''),
          'owfam-' + cleanHandle.replace(/^owfam-/, '')
        ].filter(Boolean);

        let prod = null;
        let finalHandle = cleanHandle;
        let isMetafieldJson = false;
        for (const h of handleCandidates) {
          try {
            try {
              const rMeta = await fetch(`/products/${h}?view=variants-json`);
              if (rMeta.ok) {
                const metaJson = await rMeta.json();
                if (metaJson && Array.isArray(metaJson.variants) && metaJson.variants.length) {
                  prod = metaJson;
                  finalHandle = h;
                  isMetafieldJson = true;
                }
              }
            } catch (_) {}

            if (!prod) {
              const r = await fetch(`/products/${h}.js`);
              if (r.ok) {
                prod = await r.json();
                finalHandle = h;
              }
            }
            if (prod) break;
          } catch (_) {}
        }

        if (!prod || !prod.variants || !prod.variants.length) {
          throw new Error('No variants found for ' + cleanHandle);
        }

        const sizes = {};
        const shapes = {};
        const colors = {};
        const images = {};
        const availability = {};

        const activeRugUrl = (activeBtn && (activeBtn.getAttribute('data-rug') || activeBtn.dataset.rug)) || window.activeVisualizerRugUrl || '';
        const defaultImg = (prod.featured_image && (prod.featured_image.src || prod.featured_image)) || activeRugUrl;

        if (isMetafieldJson) {
          prod.variants.forEach(v => {
            sizes[v.id] = v.size || v.title || "";
            shapes[v.id] = v.shape || "";
            colors[v.id] = v.color || "";
            let vImg = v.image_url || v.image || defaultImg;
            if (vImg && vImg.startsWith('//')) vImg = 'https:' + vImg;
            images[v.id] = vImg;
            availability[v.id] = v.available !== false;
          });
        } else {
          const sizeIndex = prod.options ? prod.options.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'size') : -1;
          const shapeIndex = prod.options ? prod.options.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'shape') : -1;
          const colorIndex = prod.options ? prod.options.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'color') : -1;

          prod.variants.forEach(v => {
            const vOpts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
            sizes[v.id] = sizeIndex !== -1 && vOpts[sizeIndex] ? vOpts[sizeIndex] : (v.option1 || v.title || "");
            shapes[v.id] = shapeIndex !== -1 && vOpts[shapeIndex] ? vOpts[shapeIndex] : (v.option2 || "");
            colors[v.id] = colorIndex !== -1 && vOpts[colorIndex] ? vOpts[colorIndex] : (v.option3 || "");

            let variantImage =
              (v.featured_image && (v.featured_image.src || v.featured_image)) ||
              defaultImg ||
              activeRugUrl ||
              '';

            if (variantImage && variantImage.startsWith('//')) {
              variantImage = 'https:' + variantImage;
            }

            images[v.id] = variantImage;
            availability[v.id] = v.available !== false;
          });
        }

        const recVariantData = {
          sizes,
          shapes,
          colors,
          images,
          availability,
          isRecommendation: true,
          productUrl: `/products/${finalHandle}`
        };

        // Determine matching variant for initial selection
        let matchedVariant = null;
        const preferredVid = (activeBtn && (activeBtn.getAttribute('data-variant-id') || activeBtn.dataset.variantId)) || window.activeVisualizerVariantId;
        if (preferredVid) {
          matchedVariant = prod.variants.find(v => String(v.id) === String(preferredVid));
        }
        if (!matchedVariant) {
          const btnSize = ((activeBtn && activeBtn.getAttribute('data-size')) || window.activeVisualizerSize || '').toLowerCase().trim();
          const btnShape = ((activeBtn && activeBtn.getAttribute('data-shape')) || window.activeVisualizerShape || '').toLowerCase().trim();
          if (btnSize || btnShape) {
            matchedVariant = prod.variants.find(v => {
              const vSize = (sizes[v.id] || '').toLowerCase().trim();
              const vShape = (shapes[v.id] || '').toLowerCase().trim();
              if (btnSize && btnShape) return vSize === btnSize && vShape === btnShape;
              if (btnSize) return vSize === btnSize;
              if (btnShape) return vShape === btnShape;
              return false;
            });
          }
        }
        if (!matchedVariant) {
          matchedVariant = prod.variants.find(v => v.available) || prod.variants[0];
        }

        window.activeVisualizerVariantData = recVariantData;

        initialVariantId = matchedVariant ? String(matchedVariant.id) : null;
        currentSidePanelSelectedVariantId = initialVariantId;

        initializeSidePanel(recVariantData);
        updateActiveSidePanelButton();

        if (isFirstLoadOfSession) {
          openSidebar();
        }
      } catch (err) {
        console.warn('[Visualizer Side Panel] Could not load variants for recommended product:', err);
        closeSidebar();
      }
    }

    new MutationObserver(() => {
      if (visualizerModal.style.display !== 'none') {
        if (!sidePanelInitialized) {
          const currentPdpHandle = window.location.pathname.includes('/products/')
            ? (window.location.pathname.split('/products/')[1] || '').split('?')[0].split('/')[0].replace(/^owfam-/, '').trim()
            : '';

          let activeBtn = window.activeVisualizerButton;
          if (!activeBtn && window.activeVisualizerProductHandle) {
            activeBtn = document.querySelector(`[data-product-handle="${window.activeVisualizerProductHandle}"][data-is-chat-card="true"], [data-product-handle="${window.activeVisualizerProductHandle}"][data-is-ai-rec="true"]`);
          }

          const isAiRec = Boolean(
            activeBtn && (
              activeBtn.getAttribute('data-is-ai-rec') === 'true' ||
              activeBtn.closest('ai-recommendations, .ai-recommendations-section, .ai-carousel-item')
            )
          );

          let targetHandle = (activeBtn && (activeBtn.getAttribute('data-product-handle') || (activeBtn.getAttribute('data-product-url') || '').split('/products/')[1]))
            || window.activeVisualizerProductHandle
            || null;
          if (!targetHandle && window.pendingSeeInRoom) {
            targetHandle = window.pendingSeeInRoom.productHandle || (window.pendingSeeInRoom.productUrl ? window.pendingSeeInRoom.productUrl.split('/products/')[1] : null);
          }
          if (targetHandle) {
            targetHandle = String(targetHandle).replace(/^owfam-/, '').replace(/^\/products\//, '').split('?')[0].split('#')[0].trim();
          }

          const isCurrentPdp = Boolean(
            currentPdpHandle &&
            !isAiRec &&
            (
              (activeBtn && (activeBtn.id === 'pdp-see-in-room' || activeBtn.closest('.pdp-view-in-room'))) ||
              (targetHandle && (targetHandle === currentPdpHandle || targetHandle === currentPdpHandle.replace(/^owfam-/, '')))
            )
          );

          if (isCurrentPdp) {
            // Standard PDP launch
            const variantInput = document.querySelector('input[name="id"], select[name="id"], .variant-selector');
            initialVariantId = variantInput ? variantInput.value : null;
            const pdpData = window.RugVariantData || window.pdpRugVariantData;
            if (pdpData && pdpData.sizes && Object.keys(pdpData.sizes).length > 0) {
              initializeSidePanel(pdpData);
            } else if (currentPdpHandle) {
              loadRecommendedProductVariants(activeBtn, currentPdpHandle);
            } else {
              initializeSidePanel();
            }
            sidePanelInitialized = true;
            isFirstLoadOfSession = true;
            openSidebar();
          } else {
            // External card launch (AI recommendation or in-chat product card)
            if (window.RugVariantData || document.querySelector('input[name="id"]')) {
              capturePdpOriginalState();
            }

            const preloaded = window.activeVisualizerVariantData;
            if (preloaded && preloaded.sizes && Object.keys(preloaded.sizes).length > 0) {
              sidePanelInitialized = true;
              isFirstLoadOfSession = true;
              preloaded.isRecommendation = true;

              let matchedVid = null;
              const preferredVid = (activeBtn && (activeBtn.getAttribute('data-variant-id') || activeBtn.dataset.variantId)) || window.activeVisualizerVariantId;
              if (preferredVid && preloaded.sizes[preferredVid]) {
                matchedVid = preferredVid;
              } else if (window.activeVisualizerSize) {
                const targetSize = String(window.activeVisualizerSize).toLowerCase().trim();
                for (const vid in preloaded.sizes) {
                  if (String(preloaded.sizes[vid]).toLowerCase().trim() === targetSize) {
                    matchedVid = vid;
                    break;
                  }
                }
              }
              if (!matchedVid) {
                for (const vid in preloaded.sizes) {
                  if (preloaded.availability && preloaded.availability[vid] !== false) {
                    matchedVid = vid;
                    break;
                  }
                }
              }
              if (!matchedVid) {
                matchedVid = Object.keys(preloaded.sizes)[0] || null;
              }

              initialVariantId = matchedVid;
              currentSidePanelSelectedVariantId = initialVariantId;

              initializeSidePanel(preloaded);
              updateActiveSidePanelButton();


                openSidebar();

            } else if (targetHandle) {
              sidePanelInitialized = true;
              isFirstLoadOfSession = true;
              vizSizeOptions.innerHTML = '';

              openSidebar();

              loadRecommendedProductVariants(activeBtn, targetHandle);
            } else {
              sidePanelInitialized = true;
              closeSidebar();
            }
          }
        }
        updateActiveSidePanelButton();
      } else {
        if (sidePanelInitialized) {
          restorePdpOriginalState();
          sidePanelInitialized = false;
          initialVariantId = null;
        }
        closeSidebar();
      }
    }).observe(visualizerModal, { attributes: true, attributeFilter: ['style', 'class'] });

    function initializeSidePanel(variantDataOverride = null) {
      vizSizeOptions.innerHTML = '';
      const sourceData = variantDataOverride || window.RugVariantData;
      
      if (sourceData) {
        const { sizes, shapes, images, availability, isRecommendation = false, productUrl = '' } = sourceData;
        const uniqueVariants = [];
        const seen = new Set();
        const hasAnyAvailable = availability && Object.values(availability).some(v => v === true);
        
        for (const variantId in sizes) {
          if (hasAnyAvailable && availability[variantId] === false) continue;
          const sz = sizes[variantId];
          const sh = shapes[variantId];
          const label = sh ? `${sz} (${sh})` : sz;
          
          if (!seen.has(label)) {
            seen.add(label);
            uniqueVariants.push({
              id: variantId,
              size: sz,
              shape: sh,
              label: label,
              image: images[variantId],
              isRecommendation,
              productUrl
            });
          }
        }

        if (uniqueVariants.length > 0) {

          uniqueVariants.forEach(v => {
            const btn = document.createElement('button');
            btn.className = 'viz-variant-btn';
            btn.dataset.variantId = v.id;
            btn.dataset.size = v.size;
            btn.dataset.shape = v.shape;
            
            const rawThumb = v.image || '';
            let thumbnailSrc = rawThumb;
            if (window.CloudImageHandler && window.CloudImageHandler.optimizeImageUrl && rawThumb) {
              thumbnailSrc = window.CloudImageHandler.optimizeImageUrl(rawThumb);
            }
            btn.innerHTML = `
              <div class="viz-variant-btn-content" style="display:flex; align-items:center; gap: 12px; width: 100%;">
                ${thumbnailSrc ? `<img src="${thumbnailSrc}" class="viz-variant-thumb" onerror="if(window.CloudImageHandler&&window.CloudImageHandler.onError){window.CloudImageHandler.onError(this,btoa(JSON.stringify([{url:'${rawThumb}'}])));}">` : `<div class="viz-variant-thumb"></div>`}
                <div style="display:flex; flex-direction:column; align-items:flex-start; flex-grow:1; gap:4px; overflow:hidden;">
                  <span class="viz-variant-label">${v.label}</span>
                  <div class="viz-variant-badges" style="display: flex; gap: 4px;">
                     ${v.id === initialVariantId ? '<span class="viz-variant-badge-original">Original</span>' : ''}
                     <span class="viz-variant-badge-current" style="display: none;">Visualising</span>
                  </div>
                </div>
                <div class="viz-variant-check" style="width:20px; height:20px; border-radius:50%; border:1px solid rgba(255,255,255,0.2); display:flex; align-items:center; justify-content:center; flex-shrink:0;">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="display:none; color:#ea580c;"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
              </div>
            `;
            
            btn.addEventListener('click', () => {
              selectVariantInSidePanel(v);
            });
            
            vizSizeOptions.appendChild(btn);
          });
        }
      } 

      // Ensure recommendation rail is loaded
      const vizRecsList = document.getElementById('viz-recommendations-list');
      if (!variantDataOverride || !vizRecsList || vizRecsList.children.length === 0) {
        loadAiRecommendations();
      }

      function loadAiRecommendations() {
        const vizRecsContainer = document.querySelector('.viz-side-panel__recommendations');
        const vizRecsList = document.getElementById('viz-recommendations-list');
        const sirBtn = document.getElementById('pdp-see-in-room');
        if (!vizRecsContainer || !vizRecsList) return;

        vizRecsContainer.style.display = 'block';

        // 1. Resolve product handle from active clicked button or page
        const activeBtn = window.activeVisualizerButton;
        let handle = (activeBtn && (activeBtn.getAttribute('data-product-handle') || (activeBtn.getAttribute('data-product-url') || '').split('/products/')[1]))
          || window.activeVisualizerProductHandle
          || (sirBtn && sirBtn.getAttribute('data-product-handle'))
          || (window.location.pathname.split('/products/')[1] || '').split('?')[0].split('/')[0];

        // 2. Resolve tenant & API base URL
        const aiEl = document.querySelector('ai-recommendations');
        // const tenant = window.DISCOVERY_AI_TENANT || (aiEl && aiEl.dataset.tenant) || 'discovery-ai';
        const tenant =
          window.DISCOVERY_AI_TENANT ||
          window.shopkeeperTenant ||
          (aiEl && aiEl.dataset.tenant) ||
          'rugtown';
        const baseUrl = window.DISCOVERY_AI_API_URL || (aiEl && aiEl.dataset.apiUrl) || 'https://api.productadvisorai.com';

        // 3. Try DOM extraction from .ai-section-grid if present on page
        const aiGridItems = document.querySelectorAll('.ai-section-grid li, .ai-section-grid .grid__item, .ai-section-grid .card-wrapper');
        const productsFromDom = [];

        aiGridItems.forEach(item => {
          const linkEl = item.querySelector('.ai-product-link, a[href*="/products/"]');
          const imgEl = item.querySelector('.ai-product-image, img');
          const titleEl = item.querySelector('.ai-product-title, .card__heading');
          const priceEl = item.querySelector('.ai-product-price, .price-item');

          if (linkEl && linkEl.href) {
            const url = linkEl.href;
            const title = titleEl ? titleEl.textContent.trim() : (imgEl ? (imgEl.alt || 'Recommended Rug') : 'Recommended Rug');
            const image = imgEl ? (imgEl.getAttribute('data-current-url') || imgEl.src) : '';
            const price = priceEl ? priceEl.textContent.trim() : '';

            if (image && !productsFromDom.some(p => p.url === url)) {
              productsFromDom.push({ title, url, image, price });
            }
          }
        });

        if (productsFromDom.length > 0) {
          renderRecommendationCards(productsFromDom.slice(0, 6));
          return;
        }

        // 4. Fetch directly from AI Recommendations API
        if (handle) {
          const apiUrl = `${baseUrl}/api/recommendations/pdp?tenant_id=${encodeURIComponent(tenant)}&product_handle=${encodeURIComponent(handle)}`;
          fetch(apiUrl)
            .then(res => res.json())
            .then(data => {
              const recs = [];
              if (data && data.sections) {
                data.sections.forEach(sec => {
                  if (sec.recommendations) {
                    sec.recommendations.forEach(r => {
                      recs.push({
                        title: r.title,
                        url: `/products/${r.handle}`,
                        image: r.image_url || r.gcs_image_url || r.primary_image_url,
                        price: r.price ? (typeof r.price === 'number' ? '$' + r.price.toFixed(2) : r.price) : ''
                      });
                    });
                  }
                });
              }
              if (recs.length > 0) {
                renderRecommendationCards(recs.slice(0, 6));
              } else {
                fetchShopifyFallbackRecs();
              }
            })
            .catch(err => {
              console.warn('[Visualizer] AI API fetch failed, trying Shopify recommendations', err);
              fetchShopifyFallbackRecs();
            });
        } else {
          fetchShopifyFallbackRecs();
        }

        function fetchShopifyFallbackRecs() {
          const pId = (activeBtn && activeBtn.getAttribute('data-product-id')) 
            || (sirBtn && sirBtn.getAttribute('data-product-id'))
            || (window.meta && window.meta.product ? window.meta.product.id : null);
          if (!pId) {
            vizRecsContainer.style.display = 'none';
            return;
          }
          fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'recommendations/products.json?product_id=' + pId + '&limit=6')
            .then(res => res.json())
            .then(data => {
              if (data.products && data.products.length > 0) {
                const recs = data.products.map(p => ({
                  title: p.title,
                  url: p.url,
                  image: p.featured_image,
                  price: p.price ? '$' + (p.price / 100).toFixed(2) : ''
                }));
                renderRecommendationCards(recs);
              } else {
                vizRecsContainer.style.display = 'none';
              }
            })
            .catch(err => {
              console.error('Error fetching fallback recommendations:', err);
              vizRecsContainer.style.display = 'none';
            });
        }

        function renderRecommendationCards(products) {
          vizRecsList.innerHTML = '';

          try {
            vizRecsList.scrollLeft = 0;
          } catch (_) {}

          enableDesktopRecommendationScrolling(vizRecsList);
          ensureDesktopRecommendationNav(vizRecsList);

          products.forEach(p => {
            const pDiv = document.createElement('div');
            pDiv.className = 'viz-rec-card';

            const img = document.createElement('img');
            const rawImageSrc = p.image || '';
            let optimizedSrc = rawImageSrc;
            if (window.CloudImageHandler && window.CloudImageHandler.optimizeImageUrl && rawImageSrc) {
              optimizedSrc = window.CloudImageHandler.optimizeImageUrl(rawImageSrc);
            }
            img.src = optimizedSrc;
            img.alt = p.title || '';
            img.className = 'viz-rec-card-thumb';

            img.onerror = function() {
              if (window.CloudImageHandler && window.CloudImageHandler.onError) {
                const fakeImagesArray = btoa(JSON.stringify([{url: rawImageSrc}]));
                window.CloudImageHandler.onError(this, fakeImagesArray, p.handle || '', p.title || '');
              } else if (rawImageSrc) {
                const clean = rawImageSrc.replace(/^https?:\/\//, "");
                this.src = `https://wsrv.nl/?url=${encodeURIComponent(clean)}&w=600&output=webp&q=80`;
              }
            };

            const info = document.createElement('div');
            info.className = 'viz-rec-card-info';

            const title = document.createElement('span');
            title.className = 'viz-rec-card-title';
            title.textContent = p.title;

            const price = document.createElement('span');
            price.className = 'viz-rec-card-price';
            price.textContent = p.price || '';

            info.appendChild(title);
            info.appendChild(price);
            pDiv.appendChild(img);
            pDiv.appendChild(info);

            pDiv.addEventListener('click', () => {
              if (typeof openSidebar === 'function') openSidebar();
              if (typeof clearSidePanelAutoClose === 'function') clearSidePanelAutoClose();
              vizRecsContainer.style.opacity = '0.5';
              const productPath = p.url.split('?')[0];
              (async () => {
                let prod = null;
                let isMetafieldJson = false;
                try {
                  const rMeta = await fetch(productPath + '?view=variants-json');
                  if (rMeta.ok) {
                    const metaJson = await rMeta.json();
                    if (metaJson && Array.isArray(metaJson.variants) && metaJson.variants.length) {
                      prod = metaJson;
                      isMetafieldJson = true;
                    }
                  }
                } catch (_) {}

                if (!prod) {
                  const r = await fetch(productPath + '.js');
                  if (r.ok) {
                    prod = await r.json();
                  }
                }

                if (!prod || !prod.variants) return;

                const sizes = {};
                const shapes = {};
                const colors = {};
                const images = {};
                const availability = {};

                const recommendationImage = p.image || '';
                const defaultImg = (prod.featured_image && (prod.featured_image.src || prod.featured_image)) || recommendationImage;

                if (isMetafieldJson) {
                  prod.variants.forEach(v => {
                    sizes[v.id] = v.size || v.title || "";
                    shapes[v.id] = v.shape || "";
                    colors[v.id] = v.color || "";
                    let vImg = v.image_url || v.image || defaultImg;
                    if (vImg && vImg.startsWith('//')) vImg = 'https:' + vImg;
                    images[v.id] = vImg;
                    availability[v.id] = v.available !== false;
                  });
                } else {
                  const sizeIndex = prod.options ? prod.options.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'size') : -1;
                  const shapeIndex = prod.options ? prod.options.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'shape') : -1;
                  const colorIndex = prod.options ? prod.options.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'color') : -1;

                  prod.variants.forEach(v => {
                    sizes[v.id] = sizeIndex !== -1 && v.options ? v.options[sizeIndex] : (v.option1 || "");
                    shapes[v.id] = shapeIndex !== -1 && v.options ? v.options[shapeIndex] : (v.option2 || "");
                    colors[v.id] = colorIndex !== -1 && v.options ? v.options[colorIndex] : (v.option3 || "");

                    let variantImage =
                      (v.featured_image && (v.featured_image.src || v.featured_image)) ||
                      defaultImg ||
                      recommendationImage ||
                      '';

                    if (variantImage && variantImage.startsWith('//')) {
                      variantImage = 'https:' + variantImage;
                    }

                    images[v.id] = variantImage;
                    availability[v.id] = v.available !== false;
                  });
                }

                  const recVariantData = {
                    sizes,
                    shapes,
                    colors,
                    images,
                    availability,
                    isRecommendation: true,
                    productUrl: p.url
                  };

                  const firstAvailable =
                    prod.variants.find(v => v.available) || prod.variants[0];

                  if (!firstAvailable) {
                    throw new Error('No usable variant found for recommended product');
                  }

                  // IMPORTANT:
                  // reset all visualizer state to the newly clicked product BEFORE
                  // rebuilding the size panel.
                  initialVariantId = String(firstAvailable.id);
                  currentSidePanelSelectedVariantId = initialVariantId;

                  window.activeVisualizerVariantId = initialVariantId;
                  window.activeVisualizerSize =
                    sizes[firstAvailable.id] || '';
                  window.activeVisualizerShape =
                    shapes[firstAvailable.id] || '';

                  window.activeVisualizerRugUrl =
                    images[firstAvailable.id] ||
                    recommendationImage ||
                    '';

                  // The newly selected recommendation is now the active
                  // product/variant source for subsequent size changes.
                  window.activeVisualizerVariantData = recVariantData;

                  initializeSidePanel(recVariantData);
                  updateActiveSidePanelButton();

                  const label =
                    sizes[firstAvailable.id]
                      ? (
                          sizes[firstAvailable.id] +
                          (
                            shapes[firstAvailable.id]
                              ? ' - ' + shapes[firstAvailable.id]
                              : ''
                          )
                        )
                      : prod.title;

                  selectVariantInSidePanel({
                    id: String(firstAvailable.id),
                    size: sizes[firstAvailable.id],
                    shape: shapes[firstAvailable.id],
                    label: label,
                    image:
                      images[firstAvailable.id] ||
                      recommendationImage ||
                      '',
                    isRecommendation: true,
                    productUrl: p.url
                  });

                  vizRecsContainer.style.opacity = '1';
                })()
                .catch(err => {
                  console.error('Failed to load new rug', err);
                  vizRecsContainer.style.opacity = '1';
                });
            });

            vizRecsList.appendChild(pDiv);
          });
          vizRecsContainer.style.display = 'flex';
          try {
            requestAnimationFrame(() => {
              vizRecsList.scrollLeft = 0;
            });
          } catch (_) {}
        }
      }
    }
    window.initializeSidePanel = initializeSidePanel;

    function updateActiveSidePanelButton() {
      const variantInput = document.querySelector('input[name="id"], select[name="id"], .variant-selector');
      const activeVid = currentSidePanelSelectedVariantId || (variantInput ? variantInput.value : null);

      const buttons = vizSizeOptions.querySelectorAll('.viz-variant-btn');
      buttons.forEach(btn => {
        const vid = btn.dataset.variantId;
        const currentBadge = btn.querySelector('.viz-variant-badge-current');

        if (vid === activeVid) {
          btn.classList.add('is-active');
          if (currentBadge) currentBadge.style.display = 'inline-block';
        } else {
          btn.classList.remove('is-active');
          if (currentBadge) currentBadge.style.display = 'none';
        }
      });
    }

    async function selectVariantInSidePanel(variantData) {
      currentSidePanelSelectedVariantId = variantData.id;
      if (typeof openSidebar === 'function') openSidebar();
      if (typeof clearSidePanelAutoClose === 'function') clearSidePanelAutoClose();

      if (!variantData.isRecommendation) {
        const variantInput = document.querySelector('input[name="id"], select[name="id"], .variant-selector');
        if (variantInput) {
          variantInput.value = variantData.id;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const btn = document.getElementById('pdp-see-in-room');
        if (btn) {
          if (variantData.size) btn.setAttribute('data-size', variantData.size);
          if (variantData.shape) btn.setAttribute('data-shape', variantData.shape);
          if (variantData.image) btn.setAttribute('data-rug', variantData.image);
        }
      }

      updateActiveSidePanelButton();

      document.dispatchEvent(new CustomEvent('visualizerVariantSelected', {
        detail: variantData
      }));
    }

    // Close side panel or modal on Escape key press
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const modal = document.getElementById('see-in-room-modal');
        if (modal && modal.style.display !== 'none') {
          const sidePanel = document.getElementById('visualizer-side-panel');
          if (sidePanel && sidePanel.getAttribute('aria-hidden') === 'false') {
            // Close side panel first if open
            const closeBtn = document.querySelector('.viz-side-panel__close');
            if (closeBtn) {
              closeBtn.click();
            } else {
              sidePanel.setAttribute('aria-hidden', 'true');
              const backdrop = document.getElementById('visualizer-side-panel-backdrop');
              if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
              modal.classList.remove('viz-side-panel-open');
            }
          } else {
            // Close the entire modal
            if (typeof window.hidePreviewModal === 'function') {
              window.hidePreviewModal();
            } else if (typeof hidePreviewModal === 'function') {
              hidePreviewModal();
            }
          }
        }
      }
    });
  }
});